import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X } from "lucide-react";
import { getConsent, saveConsent } from "../lib/consent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(true);

  useEffect(() => {
    if (!getConsent()) setVisible(true);
    const open = () => {
      const c = getConsent();
      setAnalytics(c ? !!c.analytics : true);
      setMarketing(c ? !!c.marketing : true);
      setShowPrefs(true);
      setVisible(true);
    };
    window.addEventListener("open-cookie-settings", open);
    return () => window.removeEventListener("open-cookie-settings", open);
  }, []);

  const finish = (consent) => {
    saveConsent(consent);
    setVisible(false);
    setShowPrefs(false);
  };

  const acceptAll = () => finish({ analytics: true, marketing: true });
  const rejectAll = () => finish({ analytics: false, marketing: false });
  const savePrefs = () => finish({ analytics, marketing });

  if (!visible) return null;

  return (
    <AnimatePresence>
      <motion.div
        data-testid="cookie-banner"
        initial={{ y: 120, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 120, opacity: 0 }}
        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
        className="fixed bottom-0 left-0 right-0 z-[60] p-4 md:p-6"
      >
        <div className="max-w-[1100px] mx-auto bg-[#0d0d0d]/95 backdrop-blur-xl border border-white/15 shadow-2xl">
          <div className="p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="text-[#ff3b30] mt-0.5 shrink-0">
                <Cookie size={22} />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-xl md:text-2xl mb-2">Nastavení soukromí</h3>
                <p className="text-white/60 text-sm leading-relaxed max-w-2xl">
                  Používáme nezbytné cookies pro chod webu. Se souhlasem používáme také
                  analytické cookies, které nám pomáhají web zlepšovat. Analytika se
                  <span className="text-white/80"> nespustí</span>, dokud nedáte souhlas. Více v{" "}
                  <Link to="/cookies" className="text-[#ff3b30] hover:underline">zásadách cookies</Link>.
                </p>

                <AnimatePresence>
                  {showPrefs && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="mt-5 space-y-3 border-t border-white/10 pt-5" data-testid="cookie-prefs">
                        <PrefRow
                          title="Nezbytné cookies"
                          desc="Nutné pro základní fungování webu. Nelze vypnout."
                          checked
                          disabled
                        />
                        <PrefRow
                          title="Analytické cookies"
                          desc="Měření návštěvnosti (Google Analytics) pro zlepšování webu."
                          checked={analytics}
                          onChange={() => setAnalytics((v) => !v)}
                          testid="toggle-analytics"
                        />
                        <PrefRow
                          title="Marketingové cookies"
                          desc="Slouží k cílení reklamy."
                          checked={marketing}
                          onChange={() => setMarketing((v) => !v)}
                          testid="toggle-marketing"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex flex-col sm:flex-row gap-3 mt-6">
                  <button
                    data-testid="cookie-accept-all"
                    onClick={acceptAll}
                    className="font-head uppercase tracking-wider text-sm border border-white text-white px-6 py-3 hover:bg-white hover:text-black transition-colors duration-300"
                  >
                    Přijmout vše
                  </button>
                  <button
                    data-testid="cookie-reject-all"
                    onClick={rejectAll}
                    className="font-head uppercase tracking-wider text-sm border border-white text-white px-6 py-3 hover:bg-white hover:text-black transition-colors duration-300"
                  >
                    Odmítnout vše
                  </button>
                  {showPrefs ? (
                    <button
                      data-testid="cookie-save-prefs"
                      onClick={savePrefs}
                      className="font-head uppercase tracking-wider text-sm bg-[#ff3b30] text-white px-6 py-3 hover:bg-white hover:text-black transition-colors duration-300"
                    >
                      Uložit volbu
                    </button>
                  ) : (
                    <button
                      data-testid="cookie-modify"
                      onClick={() => setShowPrefs(true)}
                      className="font-head uppercase tracking-wider text-sm text-white/60 px-6 py-3 hover:text-white transition-colors"
                    >
                      Upravit nastavení
                    </button>
                  )}
                </div>
              </div>
              <button
                data-testid="cookie-close"
                onClick={rejectAll}
                aria-label="Zavřít a odmítnout"
                className="text-white/40 hover:text-white transition-colors shrink-0"
              >
                <X size={18} />
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

const PrefRow = ({ title, desc, checked, onChange, disabled, testid }) => (
  <div className="flex items-center justify-between gap-4">
    <div>
      <div className="font-head text-sm text-white">{title}</div>
      <div className="text-white/45 text-xs mt-0.5">{desc}</div>
    </div>
    <button
      type="button"
      data-testid={testid}
      disabled={disabled}
      onClick={onChange}
      aria-pressed={checked}
      className={`relative w-12 h-6 rounded-full transition-colors shrink-0 ${
        checked ? "bg-[#ff3b30]" : "bg-white/20"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
          checked ? "translate-x-6" : "translate-x-0"
        }`}
      />
    </button>
  </div>
);
